<script lang="ts" setup>import { computed as _computed } from 'vue';

import { computed, onMounted, watch } from "vue";
import { ISOPROProductDType, ISOPROProductHeightOffsetVariantType, ISOPROProductMType, ISOPROProductSMType, ISOPROProductSMVariantType, ISOPROProductShearForceType, ISOPROProductShortElementType, ISOPROType } from "~/models";
import { useProductsStore } from "~/store";

import { AdaptedISOPROType, CategoriesList, VariantGroupKey, sortVariantKeys } from "./models";
import CategoriesTableFixedPart from "./CategoriesTableFixedPart.vue";

interface Props {
  /** */
  productType: AdaptedISOPROType;
  cellWidth?: number;
}

type HeaderKeys = Record<string, Record<VariantGroupKey, Set<string>>>;

const props = withDefaults(defineProps<Props>(), {
  cellWidth: 50,
});

const store = useProductsStore();

const headerKeys: HeaderKeys = _computed(() => {
  return Object.values(model.value).reduce((t, data) => {
    if (data) {
      Object.values(data).forEach((data) => {
        Object.values(data).forEach((data) => {
          Object.entries(data).filter(([lbc, variantCollection]) => {
            const lbcGroupLength = Object.keys(variantCollection).filter((variant) => {
              const assert = store.adaptedVariantTypes(props.productType).includes(variant as VariantGroupKey);
              return assert;
            });
            return lbcGroupLength.length;
          }).forEach(([lbc, variantCollection]) => {
            if (!t[lbc]) t[lbc] = Object.keys(variantCollection).reduce((r, variant) => {
              if (store.adaptedVariantTypes(props.productType).includes(variant as VariantGroupKey))
                r[variant as VariantGroupKey] = new Set<string>();
              return r;
            }, {} as HeaderKeys[string]);

            Object.entries(variantCollection).forEach(([variant, insSetups]) => {
              if (store.adaptedVariantTypes(props.productType).includes(variant as VariantGroupKey)) {
                if (!t[lbc][variant as VariantGroupKey])
                  t[lbc][variant as VariantGroupKey] = new Set<string>();

                insSetups.forEach(({ product }) => {
                  const hasShearForceLbc = product instanceof ISOPROProductMType
                  || product instanceof ISOPROProductDType
                  || product instanceof ISOPROProductHeightOffsetVariantType
                  || product instanceof ISOPROProductSMVariantType
                  || product instanceof ISOPROProductSMType;

                  if (hasShearForceLbc)
                  t[lbc][variant as VariantGroupKey].add(product.shearForceLoadBearingCapacityCode);
                });
              }
            })
          });
        });
      });
    }
    return t;
  }, {} as HeaderKeys);
});

const hasShearForce: boolean = _computed(() =>
  Object.values(headerKeys.value).some((variantGroup) =>
    Object.values(variantGroup).some((set) => set.size)));


const hasVariantRow = computed(() => {
  return Object.values(headerKeys.value).some((variantGroup) =>
    Object.entries(variantGroup).filter(([variant, set]) => variant !== 'none')
      .some(([_, set]) => set.size));
});

const totalColumns: number = _computed(() =>
  Object.values(headerKeys.value).reduce((t, variantGroup) => {
    const setEntities = Object.values(variantGroup).reduce((t, set) => t + Math.max(set.size, 1), 0);
    return t + setEntities;
  }, 0));

const model: CategoriesList = _computed(() =>
  store.productCategoriesCalculation as CategoriesList);

function recalculateAllCategories () {
  store.calculateAllCategories(props.productType);
}

watch(() => props.productType, () => recalculateAllCategories());

onMounted(() => {
  recalculateAllCategories();
});
</script>

<template>
  <div class="categories-table__container">
    <CategoriesTableFixedPart :model="model" :hasShearForce="hasShearForce" :hasVariants="hasVariantRow" />
    <div class="categories-table__wrapper">
      <table class="categories-table">
        <thead :style="{ height: '50px' }">
          <tr>
            <th :colspan="totalColumns" class="categories-table__group">
              Load bearing capacity
            </th>
          </tr>
          <tr>
            <th
              v-for="(variantGroup, lbc) in headerKeys"
              :colspan="Object.values(Object.values(variantGroup))
                .reduce((t, set) => t + Math.max(set.size, 1), 0)"
              :key="lbc"
              class="categories-table__group"
            >
              {{ lbc }}
            </th>
          </tr>
          <tr v-if="hasVariantRow">
            <template v-for="(variantGroup, lbc) in headerKeys" :key="lbc">
              <th
                v-for="(set, variantType) in sortVariantKeys(variantGroup)"
                :colspan="set?.size"
                :key="variantType"
                class="categories-table__group"
              >
                {{ variantType }}
              </th>
            </template>
          </tr>
          <tr v-if="hasShearForce">
            <template v-for="(variantGroup, lbc) in headerKeys" :key="lbc">
              <template v-for="(set, variant) in variantGroup" :key="variant">
                <th v-for="(sfLbc, i) in set" :key="i" class="categories-table__group">
                  {{ sfLbc }}
                </th>
              </template>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(heightCollection, height) in model" :key="height">
            <template v-for="(fpCollection, fp) in heightCollection" :key="fp">
              <tr v-for="(wsCollection, ws) in fpCollection" :key="ws">
                <template v-for="(lbcCollection, lbc) in wsCollection" :key="lbc">
                  <template v-for="(insSetupArray, variant) in lbcCollection" :key="variant">
                    <template v-if="store.adaptedVariantTypes(props.productType).includes(variant)">
                      <td
                        v-for="{ product, calculationType, calculationStrategy, category } in insSetupArray"
                        :key="product.rowKey + ws"
                        :title="`${product.rowKey + ' ' + ws}\n${calculationType} value: ${calculationStrategy}`"
                        :class="[calculationType]"
                        :style="[{ 'min-width': cellWidth + 'px' }, calculationStrategy ? 'height: inherit; ' : 'height: 31px;']"
                      >
                        {{ category }}
                      </td>
                    </template>
                  </template>
                </template>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss">
.categories-table {
  &__container {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto 1fr;

    thead {
      height: 50px;
      text-align: center;

      th.vertical {
        writing-mode: vertical-rl;
        text-orientation: mixed;
        height: 60px;
      }
    }
  }

  &__wrapper {
    overflow: auto;
  }

  &__group,
  thead {
    color: var(--color-grey-98);
    background-color: var(--bds-brand-primary-color);
  }

  td,
  th {
    height: 30px;
    padding: 5px;
  }

  tbody {
    td {
      // text-align: center;
      background-color: lightgray;
      font-size: 16px;
      font-family: monospace;
      font-weight: 500;
      text-align: center;
      user-select: none;

      &.direct {
        background-color: var(--color-green-80);
      }

      // &.indirect {
      //   background-color: var(--color-blue-67);
      // }
    }
  }

  th {
    background-color: var(--bds-brand-primary-color);
    color: var(--color-grey-98);
  }
}
</style>
